
import React, { useState, useEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { KernelSettings, AIProviderType, ProviderConfig } from '../types';
import { aiService } from '../services/aiService';
import { backendService } from '../services/backendService';

const DEFAULT_LAYOUT = [
  { i: 'infrastructure', x: 0, y: 0, w: 6, h: 9 },
  { i: 'intelligence', x: 6, y: 0, w: 6, h: 9 }
];

interface RoutingKernelProps {
  kernel: KernelSettings;
  setKernel: React.Dispatch<React.SetStateAction<KernelSettings>>;
}

const RoutingKernel: React.FC<RoutingKernelProps> = ({ kernel, setKernel }) => {
  const [activeSubTab, setActiveSubTab] = useState<'VISION' | 'ASR' | 'TTS' | 'AGENT' | 'MCP'>('VISION');
  const [fetchingModels, setFetchingModels] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<Record<string, string[]>>({});
  const [discoverStatus, setDiscoverStatus] = useState<Record<string, string>>({});

  // Draggable Layout State
  const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
  const [mounted, setMounted] = useState(false);
    
  // Custom width provider logic
  const [width, setWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('omnigate_routing_layout');
    if (saved) {
      try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
    }

    if (containerRef.current) {
      setWidth(containerRef.current.clientWidth);
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0) {
          setWidth(entry.contentRect.width);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const onLayoutChange = (layout: any, allLayouts: any) => {
    setLayouts(allLayouts);
    localStorage.setItem('omnigate_routing_layout', JSON.stringify(allLayouts));
  };

  const updateKernel = (updates: Partial<KernelSettings>) => {
    setKernel(prev => {
      const next = { ...prev, ...updates };
      aiService.updateConfig(next.aiConfig);
      return next;
    });
  };

  const updateProvider = (key: 'vision' | 'asr' | 'tts' | 'agent', updates: Partial<ProviderConfig>) => {
    const newAIConfig = {
      ...kernel.aiConfig,
      [key]: { ...kernel.aiConfig[key], ...updates }
    };
    updateKernel({ aiConfig: newAIConfig });
  };

  const handleFetchModels = async (key: 'vision' | 'asr' | 'tts' | 'agent') => {
    const config = kernel.aiConfig[key];
    if (!config.baseUrl) return;

    setFetchingModels(key);
    setDiscoverStatus(prev => ({ ...prev, [key]: 'Discovering models...' }));
    try {
      const models = await backendService.fetchProviderModels(config.baseUrl, config.apiKey);
      setAvailableModels(prev => ({ ...prev, [key]: models }));

      if (models.length === 0) {
        setDiscoverStatus(prev => ({ ...prev, [key]: 'No models found for this endpoint.' }));
        return;
      }

      const currentModel = (config.modelId || '').trim();
      const hasCurrentModel = currentModel.length > 0 && models.includes(currentModel);
      const selectedModel = hasCurrentModel ? currentModel : models[0];

      if (!hasCurrentModel) {
        updateProvider(key, { modelId: selectedModel });
      }

      setDiscoverStatus(prev => ({
        ...prev,
        [key]: hasCurrentModel
          ? `Found ${models.length} model(s). Current model is valid.`
          : `Found ${models.length} model(s). Auto-selected: ${selectedModel}`
      }));
    } catch {
      setDiscoverStatus(prev => ({ ...prev, [key]: 'Discovery failed. Check endpoint and API key.' }));
    } finally {
      setFetchingModels(null);
    }
  };

  const renderProviderConfig = (key: 'vision' | 'asr' | 'tts' | 'agent') => {
    const config = kernel.aiConfig[key];
    const modelsList = availableModels[key] || [];

    return (
      <div className="space-y-4 animate-in fade-in duration-300">
        <div className="grid grid-cols-4 gap-1">
          {['GEMINI', 'OPENAI', 'LOCAL', 'CUSTOM'].map(t => (
            <button
              key={t}
              onClick={() => updateProvider(key, { type: t as AIProviderType })}
              className={`py-1 rounded text-[8px] font-black uppercase tracking-widest border transition-all ${config.type === t ? 'bg-purple-600 border-purple-400 text-white' : 'bg-[#131722] border-[#2a2e39] text-slate-500'
                }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="space-y-3 pt-2">
          {config.type !== 'GEMINI' && (
            <>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <p className="text-[9px] font-bold text-slate-500 uppercase">Endpoint URL</p>
                  <button
                    onClick={() => handleFetchModels(key)}
                    disabled={fetchingModels === key}
                    className="text-[8px] font-black text-purple-400 hover:text-purple-300 uppercase tracking-widest transition-all"
                  >
                    {fetchingModels === key ? 'Probing...' : 'Discover Models'}
                  </button>
                </div>
                <input
                  type="text"
                  value={config.baseUrl || ''}
                  onChange={e => updateProvider(key, { baseUrl: e.target.value })}
                  placeholder="http://localhost:11434"
                  className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-slate-300 font-mono"
                />
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-bold text-slate-500 uppercase">API Key (Optional)</p>
                <input
                  type="password"
                  value={config.apiKey || ''}
                  onChange={e => updateProvider(key, { apiKey: e.target.value })}
                  className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-slate-300"
                />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-[9px] font-bold text-slate-500 uppercase">Model ID</p>
              <div className="relative">
                <input
                  list={`models-${key}`}
                  type="text"
                  value={config.modelId}
                  onChange={e => updateProvider(key, { modelId: e.target.value })}
                  className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-slate-300 font-mono"
                  placeholder="e.g. llama3"
                />
                <datalist id={`models-${key}`}>
                  {modelsList.map(m => <option key={m} value={m} />)}
                </datalist>
              </div>
              {discoverStatus[key] && (
                <p className="text-[8px] text-slate-500 font-mono pt-1">{discoverStatus[key]}</p>
              )}
            </div>
            {key === 'tts' && (
              <div className="space-y-1">
                <p className="text-[9px] font-bold text-slate-500 uppercase">Voice Profile</p>
                <input
                  type="text"
                  value={config.voiceId || ''}
                  onChange={e => updateProvider(key, { voiceId: e.target.value })}
                  placeholder="Fenrir / Alloy"
                  className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-slate-300 font-mono"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!mounted) return null;

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
      <style>{`
          .react-grid-layout { position: relative; transition: height 200ms ease; }
          .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
          .react-grid-item.cssTransforms { transition-property: transform; }
          .react-grid-item.resizing { z-index: 1; will-change: width, height; }
          .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
          .react-grid-item.react-grid-placeholder { background: rgba(168, 85, 247, 0.2); border: 2px dashed rgba(168, 85, 247, 0.5); opacity: 0.5; z-index: 2; transition: none; }
          .drag-handle { cursor: grab; }
          .drag-handle:active { cursor: grabbing; }
          .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
          .panel-wrapper:hover { border-color: rgba(168, 85, 247, 0.3); }
      `}</style>

      <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
        <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_routing_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
          <i className="fas fa-sync-alt mr-1"></i> Reset Layout
        </button>
      </div>

      <div className={`flex justify-between items-start mb-4 px-2 ${!isLocked ? 'drag-handle' : 'cursor-default'} w-full flex-shrink-0 z-10`}>
        <div>
          <h2 className="text-xl font-black text-white uppercase italic tracking-tighter">Kernel <span className="text-purple-500">& Intelligence</span></h2>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Multi-Modality Service Mesh</p>
        </div>
        <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 px-3 py-1 rounded-full">
          <i className="fas fa-microchip text-[10px] text-purple-400"></i>
          <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">Distributed Logic</span>
        </div>
      </div>

      
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
        className="layout flex-1"
        width={width}
        layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={50}
        onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
        // @ts-ignore
        draggableHandle=".drag-handle"
        margin={[12, 12]}
      >
        {/* INFRASTRUCTURE CONTROLS */}
        <div key="infrastructure" className="panel-wrapper">
          <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-server text-orange-500"></i>
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Infrastructure Controls</h3>
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0a0c10] custom-scrollbar">
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Watch Duration</p>
              <div className="grid grid-cols-3 gap-1">
                {['INDEFINITE', '30M', '4H'].map(d => (
                  <button
                    key={d}
                    onClick={() => updateKernel({ watchDuration: d as any })}
                    className={`py-1.5 rounded text-[8px] font-black uppercase tracking-widest border transition-all ${kernel.watchDuration === d ? 'bg-orange-600 border-orange-500 text-white' : 'bg-[#131722] border-[#2a2e39] text-slate-500'
                      }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-[9px] font-bold text-slate-500 uppercase flex justify-between">Vision FPS <span>{kernel.visionFps}Hz</span></p>
                <input type="range" min="0.1" max="10" step="0.1" value={kernel.visionFps} onChange={e => updateKernel({ visionFps: parseFloat(e.target.value) })} className="w-full accent-purple-500 bg-[#131722] rounded h-1 cursor-pointer" />
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-bold text-slate-500 uppercase flex justify-between">Burst Interval <span>{kernel.audioSamplingInterval}s</span></p>
                <input type="range" min="1" max="60" step="1" value={kernel.audioSamplingInterval} onChange={e => updateKernel({ audioSamplingInterval: parseInt(e.target.value) })} className="w-full accent-orange-500 bg-[#131722] rounded h-1 cursor-pointer" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Gateway Port</p>
              <input type="number" value={kernel.localPort} onChange={e => updateKernel({ localPort: parseInt(e.target.value) })} className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-sm text-purple-400 font-mono" />
            </div>
            <div className="flex items-center justify-between p-4 bg-[#131722] rounded border border-[#2a2e39]">
              <div>
                <p className="text-xs font-bold text-slate-300">External Tunnel</p>
                <p className="text-[9px] text-slate-500 uppercase font-mono">{kernel.externalUrl}</p>
              </div>
              <button onClick={() => updateKernel({ externalTunnelActive: !kernel.externalTunnelActive })} className={`w-10 h-5 rounded-full relative transition-all ${kernel.externalTunnelActive ? 'bg-green-600' : 'bg-slate-700'}`}>
                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${kernel.externalTunnelActive ? 'right-1' : 'left-1'}`}></div>
              </button>
            </div>
          </div>
        </div>

        {/* INTELLIGENCE STACK */}
        <div key="intelligence" className="panel-wrapper">
          <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex gap-2">
              {(['VISION', 'ASR', 'TTS', 'AGENT', 'MCP'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveSubTab(tab)}
                  className={`text-[9px] font-black uppercase tracking-widest pb-1 border-b-2 transition-all ${activeSubTab === tab ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-600'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-[#0a0c10] custom-scrollbar">
            {activeSubTab === 'VISION' && renderProviderConfig('vision')}
            {activeSubTab === 'ASR' && renderProviderConfig('asr')}
            {activeSubTab === 'TTS' && renderProviderConfig('tts')}
            {activeSubTab === 'AGENT' && renderProviderConfig('agent')}
            {activeSubTab === 'MCP' && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="bg-[#131722] p-4 rounded border border-[#2a2e39] space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase">Registered MCP Servers</h4>
                    <button className="text-[9px] text-purple-400 hover:text-purple-300 font-bold uppercase tracking-widest">+ Add Server</button>
                  </div>
                  <div className="space-y-2">
                    {/* Mock List for now */}
                    <div className="flex justify-between items-center p-2 bg-[#1e222d] rounded border border-[#2a2e39]">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-300">Desktop Commander</p>
                          <p className="text-[8px] font-mono text-slate-600">http://localhost:3000</p>
                        </div>
                      </div>
                      <span className="text-[8px] bg-green-500/10 text-green-500 px-1.5 py-0.5 rounded">CONNECTED</span>
                    </div>
                  </div>
                  <p className="text-[8px] text-slate-500 italic mt-2">
                    * MCP Servers are managed by the backend kernel. Configure additional servers in <code>backend/app/services/mcp_service.py</code> or via API.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </ResponsiveGridLayout>
    </div>
  );
};

export default RoutingKernel;
